/**
 * Tracks "Button Clicked" events for Mixpanel
 * Captures clicks on all buttons across the site with comprehensive data
 */

(function () {
  'use strict';

  /**
   * Checks if an element is a button
   */
  function isButton(element) {
    if (!element) return false;

    // Check if it's a button element
    if (element.tagName === 'BUTTON') return true;

    // Check classList for button-related classes
    let classList = '';
    if (element.classList && element.classList.length > 0) {
      // Use classList if available (more reliable)
      classList = Array.from(element.classList).join(' ');
    } else if (element.className) {
      // Fallback to className, ensure it's a string
      classList = typeof element.className === 'string' ? element.className : String(element.className || '');
    }

    if (classList && (classList.includes('button') || classList.includes('btn') || classList.includes('cta-button'))) {
      return true;
    }

    // Check if it's a link with button classes
    if (element.tagName === 'A') {
      return true;
    }

    // Check if it has role="button"
    if (element.getAttribute('role') === 'button') return true;

    return false;
  }

  /**
   * Finds the button element from a click event
   */
  function findButtonElement(target) {
    let element = target;

    // Traverse up the DOM to find the button
    while (element && element !== document.body) {
      if (isButton(element)) {
        return element;
      }
      element = element.parentElement;
    }

    return null;
  }

  /**
   * Gets the CTA text from a button element
   * Checks in order:
   * 1. alt attribute (button itself or in children)
   * 2. data-product-title attribute (button, parents, children, siblings)
   * 3. data-finish-name attribute (if data-product-title doesn't exist) (button, parents, children, siblings)
   * 4. aria-label attribute
   * 5. Elements with "__heading" in class name (children or siblings)
   * 6. textContent/innerText (current behavior)
   */
  function getCTAText(buttonElement) {
    if (!buttonElement) return '';

    /**
     * Searches for a data attribute in button, parents, children, and siblings
     * @param {HTMLElement} element - The button element
     * @param {string} attributeName - The data attribute name (e.g., 'data-product-title')
     * @returns {string} The attribute value or empty string
     */
    function findDataAttribute(element, attributeName) {
      if (!element) return '';

      // Check the button element itself
      const value = element.getAttribute(attributeName);
      if (value) {
        return value.trim();
      }

      // Check children (descendants)
      const childWithAttr = element.querySelector(`[${attributeName}]`);
      if (childWithAttr) {
        const childValue = childWithAttr.getAttribute(attributeName);
        if (childValue) {
          return childValue.trim();
        }
      }

      // Check parents (walking up the DOM tree)
      let parent = element.parentElement;
      while (parent && parent !== document.body) {
        const parentValue = parent.getAttribute(attributeName);
        if (parentValue) {
          return parentValue.trim();
        }
        parent = parent.parentElement;
      }

      // Check previous siblings
      let sibling = element.previousElementSibling;
      while (sibling) {
        const siblingValue = sibling.getAttribute(attributeName);
        if (siblingValue) {
          return siblingValue.trim();
        }
        // Also check children of sibling
        const siblingChild = sibling.querySelector(`[${attributeName}]`);
        if (siblingChild) {
          const siblingChildValue = siblingChild.getAttribute(attributeName);
          if (siblingChildValue) {
            return siblingChildValue.trim();
          }
        }
        sibling = sibling.previousElementSibling;
      }

      // Check next siblings
      sibling = element.nextElementSibling;
      while (sibling) {
        const siblingValue = sibling.getAttribute(attributeName);
        if (siblingValue) {
          return siblingValue.trim();
        }
        // Also check children of sibling
        const siblingChild = sibling.querySelector(`[${attributeName}]`);
        if (siblingChild) {
          const siblingChildValue = siblingChild.getAttribute(attributeName);
          if (siblingChildValue) {
            return siblingChildValue.trim();
          }
        }
        sibling = sibling.nextElementSibling;
      }

      return '';
    }

    // Priority 1: Check for alt attribute (on button itself or in children, typically images)
    const buttonAlt = buttonElement.getAttribute('alt');
    if (buttonAlt) {
      return buttonAlt.trim();
    }
    // Check for alt in children (images, etc.)
    const childWithAlt = buttonElement.querySelector('[alt]');
    if (childWithAlt) {
      const childAlt = childWithAlt.getAttribute('alt');
      if (childAlt) {
        return childAlt.trim();
      }
    }

    // Priority 2: Check for data-product-title (button, parents, children, siblings)
    const productName = findDataAttribute(buttonElement, 'data-product-title');
    if (productName) {
      return productName;
    }

    // Priority 3: Check for data-finish-name (if data-product-title doesn't exist) (button, parents, children, siblings)
    const finishName = findDataAttribute(buttonElement, 'data-finish-name');
    if (finishName) {
      return finishName;
    }

    // Priority 4: Check for aria-label
    const ariaLabel = buttonElement.getAttribute('aria-label');
    if (ariaLabel) {
      return ariaLabel.trim();
    }

    // Priority 4: Look for elements with "__heading" in class name (children or siblings)
    // Check children first
    const headingInChildren = buttonElement.querySelector('[class*="__heading"]');
    if (headingInChildren) {
      const headingText = (headingInChildren.textContent || headingInChildren.innerText || '')
        .trim()
        .replace(/\s+/g, ' ');
      if (headingText) {
        return headingText;
      }
    }

    // Check siblings
    let sibling = buttonElement.previousElementSibling;
    while (sibling) {
      if (sibling.className && typeof sibling.className === 'string' && sibling.className.includes('__heading')) {
        const siblingText = (sibling.textContent || sibling.innerText || '').trim().replace(/\s+/g, ' ');
        if (siblingText) {
          return siblingText;
        }
      }
      // Also check if sibling has a child with __heading class
      const headingInSibling = sibling.querySelector('[class*="__heading"]');
      if (headingInSibling) {
        const headingText = (headingInSibling.textContent || headingInSibling.innerText || '')
          .trim()
          .replace(/\s+/g, ' ');
        if (headingText) {
          return headingText;
        }
      }
      sibling = sibling.previousElementSibling;
    }

    // Check next siblings
    sibling = buttonElement.nextElementSibling;
    while (sibling) {
      if (sibling.className && typeof sibling.className === 'string' && sibling.className.includes('__heading')) {
        const siblingText = (sibling.textContent || sibling.innerText || '').trim().replace(/\s+/g, ' ');
        if (siblingText) {
          return siblingText;
        }
      }
      // Also check if sibling has a child with __heading class
      const headingInSibling = sibling.querySelector('[class*="__heading"]');
      if (headingInSibling) {
        const headingText = (headingInSibling.textContent || headingInSibling.innerText || '')
          .trim()
          .replace(/\s+/g, ' ');
        if (headingText) {
          return headingText;
        }
      }
      sibling = sibling.nextElementSibling;
    }

    // Priority 5: Fall back to textContent/innerText (current behavior)
    let text = buttonElement.textContent || buttonElement.innerText || '';
    text = text.trim().replace(/\s+/g, ' ');

    // If no text, try to get from title attribute
    if (!text) {
      text = buttonElement.getAttribute('title') || '';
    }

    return text;
  }

  /**
   * Finds the parent section/component
   */
  function findParentSection(buttonElement) {
    if (!buttonElement) return '';

    let element = buttonElement;

    // Traverse up the DOM to find section identifiers
    while (element && element !== document.body) {
      // Check for data-section-id attribute
      const sectionId = element.getAttribute('data-section-id');
      if (sectionId) {
        // Try to find a more descriptive identifier
        const sectionName = element.className || '';
        if (sectionName) {
          // Extract section name from class (e.g., "hero-banner", "gallery-banner-alt")
          const match = sectionName.match(/([a-z-]+(?:banner|section|block|product|card))/i);
          if (match) {
            return match[1];
          }
        }
        return `section-${sectionId}`;
      }

      // Check for custom elements (web components)
      if (element.tagName && element.tagName.includes('-')) {
        return element.tagName.toLowerCase();
      }

      // Check for section element
      if (element.tagName === 'SECTION') {
        const id = element.id || '';
        const className = element.className || '';
        if (id) return id;
        if (className) {
          const match = className.match(/([a-z-]+(?:banner|section|block))/i);
          if (match) return match[1];
        }
      }

      // Check for common section class patterns
      const className = element.className || '';
      if (className) {
        // Look for section-like class names
        const sectionPatterns = [/([a-z-]+-banner)/i, /([a-z-]+-section)/i, /([a-z-]+-block)/i, /([a-z-]+-product)/i];

        for (const pattern of sectionPatterns) {
          const match = className.match(pattern);
          if (match) {
            return match[1];
          }
        }
      }

      element = element.parentElement;
    }

    return 'unknown';
  }

  /**
   * Extracts product data from button or parent elements
   */
  function extractProductData(buttonElement) {
    if (!buttonElement) {
      return { product_id: null, variant_id: null, sku: null };
    }

    let element = buttonElement;
    const productData = { product_id: null, variant_id: null, sku: null };

    // Traverse up the DOM to find product data
    while (element && element !== document.body) {
      // Check for product data attributes
      if (element.dataset) {
        if (element.dataset.productId && !productData.product_id) {
          productData.product_id = element.dataset.productId;
        }
        if (element.dataset.variantId && !productData.variant_id) {
          productData.variant_id = element.dataset.variantId;
        }
        if (element.dataset.sku && !productData.sku) {
          productData.sku = element.dataset.sku;
        }
      }

      // If we found all product data, we can stop
      if (productData.product_id && productData.variant_id && productData.sku) {
        break;
      }

      element = element.parentElement;
    }

    return productData;
  }

  /**
   * Gets the destination URL from a button
   */
  function getDestinationURL(buttonElement) {
    if (!buttonElement) return '';

    // If it's a link, get the href
    if (buttonElement.tagName === 'A' && buttonElement.href) {
      return buttonElement.href;
    }

    // If it's in a form, get the form action
    const form = buttonElement.closest('form');
    if (form && form.action) {
      return form.action;
    }

    // Check for data-href or data-url attributes
    const dataHref = buttonElement.getAttribute('data-href') || buttonElement.getAttribute('data-url');
    if (dataHref) {
      return dataHref;
    }

    // If no URL found yet, search through parent elements for an <a> tag
    let parentElement = buttonElement.parentElement;
    while (parentElement && parentElement !== document.body) {
      if (parentElement.tagName === 'A' && parentElement.href) {
        return parentElement.href;
      }
      parentElement = parentElement.parentElement;
    }

    return '';
  }

  /**
   * Searches through children of an element for caption, title, and subheading
   */
  function searchChildrenForTextElements(parentElement, result) {
    if (!parentElement || !parentElement.children) return;

    // Search through all children (and their descendants)
    const children = parentElement.querySelectorAll('*');
    for (const child of children) {
      const className = child.className || '';
      const text = (child.textContent || child.innerText || '').trim().replace(/\s+/g, ' ');

      // Check if any class contains our target keywords
      if (className && typeof className === 'string') {
        const classes = className.split(/\s+/);
        for (const cls of classes) {
          const lowerCls = cls.toLowerCase();
          if (lowerCls.includes('caption') && !result.caption) {
            result.caption = text;
          }
          if (lowerCls.includes('title') && !result.title) {
            result.title = text;
          }
          if (lowerCls.includes('subheading') && !result.subheading) {
            result.subheading = text;
          }
          if (lowerCls.includes('subtitle') && !result.subheading) {
            result.subheading = text;
          }
        }
      }

      // Also check classList if available
      if (child.classList) {
        for (const cls of child.classList) {
          const lowerCls = cls.toLowerCase();
          if (lowerCls.includes('caption') && !result.caption) {
            result.caption = text;
          }
          if (lowerCls.includes('title') && !result.title) {
            result.title = text;
          }
          if (lowerCls.includes('subheading') && !result.subheading) {
            result.subheading = text;
          }
          if (lowerCls.includes('subtitle') && !result.subheading) {
            result.subheading = text;
          }
        }
      }

      // Stop early if we've found all three
      if (result.caption && result.title && result.subheading) {
        break;
      }
    }
  }

  /**
   * Finds caption, title, and subheading text from parent elements in a single pass
   * Returns an object with caption, title, and subheading properties
   */
  function findTextElements(buttonElement) {
    const result = {
      caption: '',
      title: '',
      subheading: '',
    };

    if (!buttonElement) return result;

    let element = buttonElement;

    // Traverse up the DOM once to find all three elements
    while (element && element !== document.body) {
      const className = element.className || '';
      const text = (element.textContent || element.innerText || '').trim().replace(/\s+/g, ' ');

      // Check if this element has a class containing "content"
      let hasContentClass = false;
      if (className && typeof className === 'string') {
        const classes = className.split(/\s+/);
        for (const cls of classes) {
          if (cls.toLowerCase().includes('__content')) {
            hasContentClass = true;
            break;
          }
        }
      }
      if (!hasContentClass && element.classList) {
        for (const cls of element.classList) {
          if (cls.toLowerCase().includes('__content')) {
            hasContentClass = true;
            break;
          }
        }
      }

      // If this element has a "content" class, search through its children
      if (hasContentClass) {
        searchChildrenForTextElements(element, result);
      }

      // Check if any class contains our target keywords on the element itself
      if (className && typeof className === 'string') {
        const classes = className.split(/\s+/);
        for (const cls of classes) {
          const lowerCls = cls.toLowerCase();
          if (lowerCls.includes('caption') && !result.caption) {
            result.caption = text;
          }
          if (lowerCls.includes('title') && !result.title) {
            result.title = text;
          }
          if (lowerCls.includes('subheading') && !result.subheading) {
            result.subheading = text;
          }
          if (lowerCls.includes('subtitle') && !result.subheading) {
            result.subheading = text;
          }
        }
      }

      // Also check classList if available
      if (element.classList) {
        for (const cls of element.classList) {
          const lowerCls = cls.toLowerCase();
          if (lowerCls.includes('caption') && !result.caption) {
            result.caption = text;
          }
          if (lowerCls.includes('title') && !result.title) {
            result.title = text;
          }
          if (lowerCls.includes('subheading') && !result.subheading) {
            result.subheading = text;
          }
          if (lowerCls.includes('subtitle') && !result.subheading) {
            result.subheading = text;
          }
        }
      }

      // Stop early if we've found all three
      if (result.caption && result.title && result.subheading) {
        break;
      }

      element = element.parentElement;
    }

    return result;
  }

  /**
   * Gets all button texts from a ul element
   */
  function getSuggestionGroup(buttonElement) {
    if (!buttonElement) return [];

    // Find the parent ul element
    let element = buttonElement;
    let ulElement = null;

    while (element && element !== document.body) {
      if (element.tagName === 'UL') {
        ulElement = element;
        break;
      }
      element = element.parentElement;
    }

    if (!ulElement) {
      return [];
    }

    // Get all buttons/links within the ul
    const buttons = ulElement.querySelectorAll('button, a[role="button"], a.button, a.btn, a.cta-button');
    const suggestionGroup = [];

    buttons.forEach((btn) => {
      const text = getCTAText(btn);
      if (text) {
        suggestionGroup.push(text);
      }
    });

    return suggestionGroup;
  }

  /**
   * Tracks button clicks
   */
  function trackButtonClick(event) {
    // Find the button element
    const buttonElement = findButtonElement(event.target);

    if (!buttonElement) {
      return;
    }

    try {
      // Extract all the data we need
      const ctaText = getCTAText(buttonElement);
      const section = findParentSection(buttonElement);
      const destinationUrl = getDestinationURL(buttonElement);
      const productData = extractProductData(buttonElement);
      const textElements = findTextElements(buttonElement);

      // Check if Mixpanel is available
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return;
      }

      // Skip tracking for bots
      if (typeof isBot === 'function' && isBot()) {
        return;
      }

      // Check if the button title is "Suggested Searches"
      const title = textElements.title || '';
      if (title && (title === 'Suggested Searches' || title.toLowerCase().includes('suggested search'))) {
        const suggestionGroup = getSuggestionGroup(buttonElement);

        // Track the Suggested Search Clicked event
        mixpanel.track('Suggested Search Clicked V.1', {
          'Suggestion Clicked': ctaText || '',
          'Suggestion Group': suggestionGroup,
          'User Agent': navigator.userAgent,
        });
      }

      // Skip tracking if destination URL is empty string (but allow javascript: URLs)
      if (destinationUrl === '') {
        return;
      }

      // Skip tracking if destination URL includes "cart"
      if (destinationUrl.toLowerCase().includes('cart')) {
        return;
      }

      // Skip tracking if destination URL contains "page="
      if (destinationUrl.includes('page=')) {
        return;
      }

      // Build event properties for regular Button Clicked event
      const eventProperties = {
        'CTA Text': ctaText || '',
        Section: section || 'unknown',
        'Destination URL': destinationUrl || '',
        'Current URL': window.location.href || '',
        'Page Title': document.title || '',
      };

      // Only include product data if it exists
      if (productData.product_id) {
        eventProperties['Product ID'] = productData.product_id;
      }
      if (productData.variant_id) {
        eventProperties['Variant ID'] = productData.variant_id;
      }
      if (productData.sku) {
        eventProperties['SKU'] = productData.sku;
      }

      // Only include text elements if they exist
      if (textElements.caption) {
        eventProperties['Caption'] = textElements.caption;
      }
      if (textElements.title) {
        eventProperties['Title'] = textElements.title;
      }
      if (textElements.subheading) {
        eventProperties['Subheading'] = textElements.subheading;
      }

      // Always include user agent
      eventProperties['User Agent'] = navigator.userAgent;

      // Track the regular Button Clicked event (bot check already done above)
      mixpanel.track('Button Clicked V.1', eventProperties);
    } catch (error) {
      // Error tracking Button Clicked
    }
  }

  /**
   * Initialize tracking for button clicks
   */
  function initButtonClickTracking() {
    // Use event delegation to handle clicks on all buttons
    // This works for dynamically loaded buttons too
    // Listen for both regular clicks and middle clicks (auxclick)
    document.addEventListener('click', trackButtonClick, true);
    document.addEventListener('auxclick', trackButtonClick, true);
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initButtonClickTracking);
  } else {
    initButtonClickTracking();
  }
})();
